import { type } from 'arktype';

/**
 * Shift rules define when and how a shift template applies.
 *
 * All fields are required for MVP:
 * - `weekday`: Array of ISO weekday indices (0=Monday ... 6=Sunday)
 * - `capacity`: Number of employees needed (>= 0)
 * - `requiredSkills`: Array of skill IDs (can be empty array)
 * - `allowOverlap`: Optional flag; when true, assignments can overlap in time
 *
 * @example
 * {
 *   weekday: [0, 1, 2, 3, 4],  // Monday-Friday
 *   capacity: 3,
 *   requiredSkills: ["skill-id-1", "skill-id-2"]
 * }
 */
/**
 * Structured skill requirement: a skill plus a minimum proficiency threshold.
 *
 * `minRating` is normalized to `[0, 1]` — the same scale as `person_skills.rating`.
 * `minRating: 0` means presence-only (employee just needs to hold the skill).
 */
export const shiftSkillRequirementSchema = type({
  skillId: type('string').configure({
    description: 'ID of the required skill.',
    examples: ['skill_abc123'],
  }),
  minRating: type('0<=number<=1').configure({
    description: 'Minimum proficiency rating required, normalized to [0, 1]. 0 means presence-only.',
    examples: [0.6],
  }),
}).configure({
  description: 'A required skill with a minimum proficiency threshold.',
});

export type ShiftSkillRequirement = typeof shiftSkillRequirementSchema.t;

/**
 * A single entry of `rules.requiredSkills`: either a legacy plain skill ID
 * (presence-only) or a structured requirement with a minimum rating.
 */
export const shiftSkillEntrySchema = type('string').or(shiftSkillRequirementSchema);

export type ShiftSkillEntry = typeof shiftSkillEntrySchema.t;

export const shiftRulesSchema = type({
  weekday: type('number[]').configure({
    description: 'ISO weekday indices where the shift applies (0=Monday … 6=Sunday).',
    examples: [[0, 1, 2, 3, 4]],
  }),
  capacity: type('number>=0').configure({
    description: 'Number of employees required for the shift.',
    examples: [3],
  }),
  requiredSkills: shiftSkillEntrySchema.array().configure({
    description:
      'Skills required for assignment to this shift. Each entry is either a plain skill ID (presence-only) or a structured requirement with a minimum rating. Use an empty array when no skills are required.',
    examples: [['skill_abc123', { skillId: 'skill_def456', minRating: 0.6 }]],
  }),
  'allowOverlap?': type('boolean').configure({
    description: 'When true, assignments for this shift may overlap in time with other shifts.',
    examples: [false],
  }),
}).configure({
  description: 'Rules describing when and how a shift template applies (weekdays, capacity, required skills, overlap allowance).',
});

export type ShiftRules = typeof shiftRulesSchema.t;

const isValidWeekday = (value: number): boolean => Number.isInteger(value) && value >= 0 && value <= 6;

/**
 * Validate shift rules with weekday range enforcement.
 * Returns a normalized value when valid.
 */
export function validateShiftRules(rules: unknown): {
  isValid: boolean;
  errors: string[];
  value?: ShiftRules;
} {
  const result = shiftRulesSchema(rules);
  if (result instanceof type.errors) {
    return { isValid: false, errors: [result.summary] };
  }

  // Validate weekday values are in valid range (0-6)
  if (result.weekday.length > 0) {
    const invalidWeekdays = result.weekday.filter(day => !isValidWeekday(day));
    if (invalidWeekdays.length > 0) {
      return {
        isValid: false,
        errors: [`Invalid weekday values: ${invalidWeekdays.join(', ')} (expected 0-6)`],
      };
    }
  }

  return { isValid: true, errors: [], value: result };
}

/**
 * Validate a standalone `requiredSkills` input value (top-level convenience
 * field or the `requiredSkills` piece of definition rules) against the entry
 * union. `null`/`undefined` are valid ("no requirements").
 */
export function validateShiftSkillEntries(value: unknown): { isValid: boolean; errors: string[]; value: ShiftSkillEntry[] | null } {
  if (value === null || value === undefined) {
    return { isValid: true, errors: [], value: null };
  }

  const result = shiftSkillEntrySchema.array()(value);
  if (result instanceof type.errors) {
    return { isValid: false, errors: [result.summary], value: null };
  }

  return { isValid: true, errors: [], value: result };
}

/**
 * Normalise a single `requiredSkills` entry to its structured form.
 * Plain skill IDs become presence-only requirements (`minRating: 0`).
 */
export function normaliseSkillEntry(entry: ShiftSkillEntry): ShiftSkillRequirement {
  return typeof entry === 'string' ? { skillId: entry, minRating: 0 } : entry;
}

const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

const clampRating = (value: number): number => Math.min(1, Math.max(0, value));

function parseSkillEntry(entry: unknown): ShiftSkillRequirement | undefined {
  if (typeof entry === 'string') {
    return entry.length > 0 ? { skillId: entry, minRating: 0 } : undefined;
  }
  if (typeof entry === 'object' && entry !== null && 'skillId' in entry) {
    const { skillId } = entry as { skillId: unknown };
    if (typeof skillId !== 'string' || skillId.length === 0) return undefined;
    const minRating = 'minRating' in entry ? (entry as { minRating: unknown }).minRating : undefined;
    return { skillId, minRating: isFiniteNumber(minRating) ? clampRating(minRating) : 0 };
  }
  return undefined;
}

/**
 * THE only way to read `requiredSkills` off a shift `rules` blob.
 *
 * Tolerant reader for persisted JSONB: coerces `null`/`undefined`/non-array
 * values to an empty list, accepts legacy `string[]` data, structured entries,
 * and mixed arrays, and silently drops garbage entries. Ratings outside `[0, 1]`
 * are clamped (write paths reject them; the reader stays lenient).
 */
export function getRequiredSkillEntries(rules: unknown): ShiftSkillRequirement[] {
  if (typeof rules !== 'object' || rules === null || !('requiredSkills' in rules)) return [];
  const { requiredSkills } = rules as { requiredSkills: unknown };
  if (!Array.isArray(requiredSkills)) return [];
  return requiredSkills.map(parseSkillEntry).filter((entry): entry is ShiftSkillRequirement => entry !== undefined);
}
